import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import {
  cacheOwner,
  getCached,
  setCached,
  CACHE_KEYS,
} from "../utils/sessionCache";
import {
  getTnStatus,
  connectTnEnVentana,
  abrirVentanaOAuth,
} from "../lib/tiendaNube";
import { traerCatalogoAlConectar } from "../lib/traerCatalogoAlConectar";
import { loadOnboardingSeen, isSeen, markSeen } from "../lib/onboardingSeen";
import type { OnboardingKey } from "../lib/onboardingSeen";
import { fetchHotSaleCoupon } from "../lib/hotSaleCoupon";
import { estadoDeCreditos } from "../lib/creditsStatus";
import { correspondeEncuesta } from "../lib/encuestaFeedback";
import { correspondeCalificarTiendanube } from "../lib/calificacionTiendanube";
import { USE_ATLAS_BACKEND } from "../config";
// Dar un paso por hecho en local (sólo dev; en el build es `false` y estos `if`
// se podan). Ver `lib/devHomeSetup`.
import { pasoForzadoHecho, pasoForzadoPendiente } from "../lib/devHomeSetup";
import { incluyeLlamadaDeCapacitacion } from "../lib/checkoutPlans";
import { useEsMobile } from "./useEsMobile";

/** Cuál de los carteles de home corresponde mostrar (o ninguno). */
export type HomeBannerKind =
  /* Acá estaba "upsell", el de la prueba gratis. Lo reemplazó la barra superior de
     créditos — ADR-2026-08-03-barra-creditos-reemplaza-upsell.

     Y acá estaban "brand", "cuestionario" y "tiendanube": tres carteles sueltos que
     salían de a uno y en orden. Los dos que quedan son AHORA UN SOLO objeto con
     progreso ("setup") — ADR-2026-08-27-tarjeta-configuracion-cuenta-paga. */
  | "setup"
  | "setup_ok"
  /** Encuesta de feedback (+10 imágenes de premio). Se había ido del recorrido con la
      unificación de los otros dos y VUELVE con un piso de uso, detrás de la tarjeta de
      configuración y de su festejo —
      ADR-2026-09-21-la-encuesta-vuelve-a-la-home-con-piso-de-uso. */
  | "cuestionario"
  /** "Calificanos en Tiendanube" (+10 imágenes, una vez). El escalón siguiente a la
      encuesta: sale sólo con ella respondida, la tienda conectada y más de 50 creaciones —
      ADR-2026-10-12-premio-por-calificar-en-tiendanube. */
  | "calificar_tiendanube"
  /** Plan ULTRA que todavía no descartó ni agendó la llamada de capacitación incluida.
      Va PRIMERO, incluso antes de la configuración (ADR-2026-08-25-banner-llamada-capacitacion-ultra). */
  | "capacitacion"
  /** "Probá Atlas en tu computadora": sólo en teléfono, y sólo cuando la configuración ya
      no pide nada (ADR-2026-08-25-cartel-probar-atlas-en-escritorio). */
  | "escritorio"
  | null;

/** Los pasos de la configuración de la cuenta paga, en el orden en que se hacen. */
export type SetupPasoId = "marca" | "tiendanube";

/**
 * Qué pasos se le piden HOY al usuario, en orden. Es el interruptor de la tarjeta:
 * agregar o sacar un `id` de acá lo suma o lo baja del recorrido, sin tocar nada más
 * —el copy vive en `HomeSetupCard` y la resolución de cada paso, más abajo—.
 *
 * Sacar un `id` de acá lo baja del recorrido sin tocar nada más: su cableado
 * —resolución, modal, flags— queda en pie y, mientras esté afuera, ni siquiera se
 * consulta su estado.
 */
const PASOS_ACTIVOS: SetupPasoId[] = ["marca", "tiendanube"];

/**
 * Qué pasos se pueden dejar para después ("Ahora no") —
 * ADR-2026-09-15-ahora-no-en-tienda-nube.
 *
 * Sólo Tienda Nube: conectar la tienda es útil pero no todo el mundo tiene una, y
 * a quien no la tiene la tarea le queda pendiente para siempre sin forma de
 * sacársela de encima. Describir la marca NO es omitible: es de lo que depende que
 * las fotos salgan con la identidad del usuario, o sea el motivo por el que pagó.
 */
const PASOS_OMITIBLES: SetupPasoId[] = ["tiendanube"];

/** La llave de `onboarding_seen` donde queda registrado el "Ahora no" de cada paso. */
const LLAVE_OMITIDO: Partial<Record<SetupPasoId, OnboardingKey>> = {
  tiendanube: "setup_tiendanube_omitido",
};

export type SetupPaso = {
  id: SetupPasoId;
  /** Resuelto: hecho de verdad, o dado por resuelto (ver `omitido`). */
  hecho: boolean;
  /**
   * Se dio por resuelto SIN hacerlo: el usuario apretó "Ahora no"
   * (ADR-2026-09-15-ahora-no-en-tienda-nube). La tarjeta lo dibuja como "Tarea
   * omitida" y no como "Tarea completada" — decir "completada" de algo que el
   * usuario decidió no hacer es mentirle en su propia pantalla. Por eso tampoco
   * suma al contador ni a la barra: `omitido` implica `hecho`, así que quien
   * cuente tareas completadas tiene que filtrar `hecho && !omitido`.
   */
  omitido: boolean;
  /** Se puede dejar para después: la tarjeta le dibuja el "Ahora no". */
  omitible: boolean;
};

/** Estado de la suscripción que amerita un banner informativo en la home. */
export type SubscriptionStatus = "cancelled" | "paused" | null;

export type HomeBannersState = {
  /**
   *  - "setup": usuario pago con algún paso de configuración pendiente. Qué paso
   *    es lo dice `setup`; la tarjeta los muestra a los dos con su progreso.
   *  - "setup_ok": los terminó (y venía haciéndolos) → felicitación, una vez.
   *  - "cuestionario": cuenta paga con la configuración ya resuelta, que generó al
   *    menos `MINIMO_GENERADAS_PARA_ENCUESTA` y todavía no respondió la encuesta
   *    (`company_profiles.cuestionario_respondido`).
   *  - "calificar_tiendanube": cuenta paga con la encuesta YA respondida, Tienda Nube
   *    conectada y más de `MINIMO_GENERADAS_PARA_CALIFICAR` creaciones, que todavía no
   *    cobró el premio (`company_profiles.calificacion_tiendanube_premiada`).
   *  - "capacitacion": usuario de plan PRO o ULTRA que todavía no descartó ni agendó la
   *    llamada de capacitación incluida. PRIMERO en la cadena, antes que la
   *    configuración (ADR-2026-08-25-banner-llamada-capacitacion-ultra).
   *  - "escritorio": usuario pago con la configuración resuelta, EN TELÉFONO
   *    (<640px) y que no descartó el cartel — invitación a usar Atlas en una
   *    computadora. Único `kind` que depende del dispositivo.
   *  - null: no corresponde ninguno (o aún cargando).
   */
  kind: HomeBannerKind;
  /**
   * Los pasos de la configuración de cuenta paga, resueltos y EN ORDEN. `null`
   * cuando no aplica (plan gratis, o todavía cargando). Se expone siempre que
   * haya `kind: "setup"`.
   */
  setup: SetupPaso[] | null;
  /**
   * Saldo de créditos (`imagenes_disponibles`: plan + regalos). Se expone para **todos** los
   * planes, no sólo el gratis: el aviso de "sin créditos" / "se te están por
   * acabar" vale para cualquiera (ADR-2026-07-30-solo-criterio-saldo-toda-app).
   */
  freeImagesRemaining: number | null;
  /**
   * Capacidad del plan (`max_fotos_mes`), el denominador del umbral del 5%. Ver
   * `lib/creditsStatus`.
   */
  creditsTotal: number | null;
  /** Empresa del usuario. */
  idCompany: string | null;
  /**
   * País del usuario === Argentina. Ya NO cambia el copy del upsell (ADR-2026-07-30-banner-home-habla-descuento-real:
   * el banner habla del descuento real, que es el mismo para todos); queda
   * porque es contexto barato de tener resuelto.
   */
  isArgentina: boolean;
  /**
   * Descuento vigente en % entero (ej. `50`), del mismo cupón invisible que
   * aplica el catálogo (`lib/hotSaleCoupon`). `null` si no hay cupón activo — y
   * entonces el banner NO promete ningún descuento. Solo se resuelve para
   * "upsell". Ver ADR-2026-07-30-banner-home-habla-descuento-real.
   */
  discountPercent: number | null;
  /**
   * Estado de la suscripción (INDEPENDIENTE de `kind`):
   *  - "cancelled": hay una cancelación programada a futuro.
   *  - "paused": la suscripción está pausada (reanuda a futuro).
   *  - null: ninguno.
   */
  status: SubscriptionStatus;
  /** Fecha de cancelación formateada (solo si status === "cancelled"). */
  cancelDate: string | null;
  /** Fecha de reanudación formateada (solo si status === "paused"). */
  resumeDate: string | null;
  /**
   * Motivo de la cancelación (`company_profiles.trigger_cancelacion`). Solo
   * relevante si status === "cancelled". Valor conocido: "fondos_insuficientes"
   * (el último pago fue rechazado por falta de fondos) → la UI muestra un copy
   * distinto (por rechazo de pago, con reintento automático). `null` = baja
   * voluntaria u otro motivo (copy default).
   */
  cancelTrigger: string | null;
  /**
   * `true` cuando el hook ya sabe qué banner corresponde (aunque la respuesta sea
   * "ninguno") — ADR-2026-08-03-home-aparece-entera-o-skeleton. Opcional porque el
   * estado interno no lo lleva: lo agrega el hook al devolver, igual que los tres
   * callbacks de abajo.
   */
  resuelto?: boolean;
  /** Conecta Tiendanube (OAuth popup) sobre la marca default y marca el paso al
   *  éxito. Lanza si el user cancela / popup bloqueado (el paso queda pendiente). */
  connectTiendanube?: () => Promise<void>;
  /** Cierra la felicitación. Ya quedó marcada como vista al mostrarse, así que
   *  esto sólo la baja de la pantalla. */
  cerrarCelebracion?: () => void;
  /**
   * "Ahora no": deja el paso para después. Persiste por cuenta y reevalúa la
   * tarjeta en el momento — si era la única tarea pendiente, la tarjeta se baja
   * sola. Sólo tiene efecto sobre un paso `omitible`.
   */
  omitirPaso?: (id: SetupPasoId) => void;
  /** Descarta el banner de la llamada de capacitación Ultra — por usuario, en la
   *  DB (`onboarding_seen`). Lo llaman "No me interesa" Y "Agendar": las dos
   *  acciones dan el cartel por resuelto. */
  dismissCapacitacion?: () => void;
  /** Descarta el banner "Probá Atlas en tu computadora" ("No me interesa" o
   *  cerrar la pantalla) — por usuario, en la DB (`onboarding_seen`). */
  dismissEscritorio?: () => void;
  /**
   * La encuesta quedó respondida: baja el cartel y reevalúa la cadena.
   *
   * NO hay un "descartar" equivalente: el cartel se baja RESPONDIENDO y nada más
   * (ADR-2026-09-21-la-encuesta-vuelve-a-la-home-con-piso-de-uso), que es como se
   * comportaba antes. El flag real (`cuestionario_respondido`) lo escribe el backend
   * al acreditar el premio; esto es lo que evita esperar a la próxima carga.
   */
  dismissCuestionario?: () => void;
  /**
   * Tocó "Hecho" en el modal de calificar en Tiendanube: baja el cartel y reevalúa la
   * cadena. Igual que la encuesta, no hay "descartar": el cartel se va reclamando.
   */
  dismissCalificarTiendanube?: () => void;
};

/* Acá vivía `MIN_IMAGENES_UPSELL`: cuántas imágenes tenía que generar un usuario de
   prueba gratis para ver el banner de "suscribite a un plan". `principal` lo había
   bajado de 5 a 3 (ADR-2026-08-03-upsell-de-home-a-las-3-imagenes) porque con 5 el
   aviso llegaba con la mitad de la prueba ya gastada. Se va con el banner
   (ADR-2026-08-05-la-barra-de-creditos-le-gana-al-upsell): quien avisa ahora es la
   barra de créditos, que se dispara por SALDO y no por cantidad generada — o sea,
   antes todavía. */

/**
 * Estado que dura lo que la CARGA de la página (no la sesión ni la cuenta) —
 * mismo patrón que la barra de créditos con su descarte.
 *
 * `celebrandoEnEstaCarga`: el id del usuario que acaba de terminar la
 *   configuración; la felicitación ya quedó marcada como vista en la DB, y esto la
 *   mantiene en pantalla mientras navega, en vez de desaparecérsele en la primera
 *   re-evaluación. Guarda el id y no un booleano: otra pestaña puede cambiar la
 *   sesión sin recargar ésta, y el festejo de la cuenta anterior no le corresponde
 *   a la nueva.
 *
 * Módulo y no estado del hook a propósito: `HomeBannersView` se monta DOS veces
 * por pantalla (mobile + desktop) y el hook se rehace al navegar entre pantallas;
 * un `useState` daría dos verdades distintas y se perdería al cambiar de ruta.
 */
let celebrandoEnEstaCarga: string | null = null;

const EMPTY: HomeBannersState = {
  kind: null,
  setup: null,
  freeImagesRemaining: null,
  creditsTotal: null,
  idCompany: null,
  isArgentina: false,
  discountPercent: null,
  status: null,
  cancelDate: null,
  resumeDate: null,
  cancelTrigger: null,
};

// Mismo formato que el Paywall ("20 de julio de 2026").
const formatDate = (raw: unknown): string | null => {
  if (!raw) return null;
  const d = new Date(raw as string);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("es-AR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/**
 * Capacidad por plan para el fallback de dev (ADR-2026-07-30-panel-dev-plan-creditos-generadas y ADR-2026-07-30-solo-criterio-saldo-toda-app): sin empresa no hay
 * `max_fotos_mes` que leer, y sin denominador el umbral del 5% no se puede
 * calcular. Son los valores del catálogo real.
 */
const isFuture = (raw: unknown): boolean => {
  if (!raw) return false;
  const d = new Date(raw as string);
  return !isNaN(d.getTime()) && d.getTime() > Date.now();
};

/**
 * Resuelve qué banner de home mostrar, con las MISMAS condiciones que la
 * pantalla clásica de selección de productos. Fuente única para que el clásico
 * y la Interfaz V2 muestren exactamente los mismos banners.
 *
 * Carteles de la home (`kind`, mutuamente excluyentes). El orden de esta lista ES
 * la prioridad: el primero que aplica gana y corta la evaluación.
 *  - Plan gratis → NINGUNO: su aviso es la barra de créditos, no un cartel
 *    (ADR-2026-08-05-la-barra-de-creditos-le-gana-al-upsell). Todavía no compró:
 *    pedirle que configure la cuenta es pedirle trabajo antes de la decisión.
 *  - Plan PRO o ULTRA + sin descartar/agendar → "capacitacion" (agendá tu llamada de
 *    capacitación incluida). PRIMERO de todos, incluso antes de la configuración:
 *    es una prestación ya paga del plan y agendarla vale más cuanto más cerca del
 *    alta (ADR-2026-08-25-banner-llamada-capacitacion-ultra).
 *  - Plan pago con algún paso pendiente → "setup" (la tarjeta con los dos).
 *  - Plan pago con todo hecho, viniendo de tenerlo pendiente → "setup_ok".
 *  - Plan pago con la configuración resuelta, ≥30 creaciones y la encuesta sin
 *    responder → "cuestionario" (ADR-2026-09-21-la-encuesta-vuelve-a-la-home-con-piso-de-uso).
 *    Detrás de la configuración a propósito: la encuesta no le roba la atención a una
 *    tarea a medias, que es por lo que se había dado de baja.
 *  - Plan pago con la encuesta YA respondida, Tienda Nube conectada, >50 creaciones y el
 *    premio sin cobrar → "calificar_tiendanube"
 *    (ADR-2026-10-12-premio-por-calificar-en-tiendanube). Como responder la encuesta
 *    reevalúa la cadena, a quien ya cumple el resto le aparece en el momento.
 *  - Plan pago con la configuración resuelta + EN TELÉFONO → "escritorio" (probá
 *    Atlas en tu computadora). Sólo abajo de 640px: en una compu el mensaje no
 *    aplica (ADR-2026-08-25-cartel-probar-atlas-en-escritorio). Va DESPUÉS de la
 *    configuración y no en el medio: la tarjeta es un recorrido con progreso y un
 *    cartel promocional en el medio lo cortaría (decisión del merge del 2026-09-15,
 *    ver ADR-2026-09-15-merge-onboarding-usuarios-pagos-a-principal).
 *
 * Los pasos ya NO son tres carteles que salen de a uno (ADR-889): son UNA tarjeta
 * con progreso — ADR-2026-08-27-tarjeta-configuracion-cuenta-paga. La encuesta de
 * feedback salió del recorrido: no es configuración de la cuenta y competía por la
 * misma atención que los dos pasos que sí lo son.
 *
 * Estado de suscripción (`status`, ortogonal a `kind`):
 *  - Cancelación programada a futuro → "cancelled".
 *  - Reanudación programada a futuro (pausa) → "paused".
 * El consumidor decide si renderizar el banner de estado (ver `HomeBannersView`
 * con `showStatus`); hoy solo se muestra en mobile.
 *
 * @param opts.marcaRecienCompletada da el paso de marca por HECHO sin consultar
 *   nada (al volver de `/redes-sociales/descripcion-brand`). El análisis lo
 *   escribe un webhook que puede tardar, así que sin esto el usuario vuelve a la
 *   home y ve el paso que acaba de completar todavía pendiente. Antes esto
 *   escondía la cadena ENTERA (`suppressBrand`) y el resultado era peor: no veía
 *   nada, y el paso siguiente recién aparecía en la próxima carga.
 */
export function useHomeBanners(opts?: {
  marcaRecienCompletada?: boolean;
}): HomeBannersState {
  const marcaRecienCompletada = opts?.marcaRecienCompletada ?? false;
  /**
   * Corte de teléfono (<640px) para el banner "escritorio". Es reactivo, así que
   * cruzar el breakpoint reevalúa la cadena: es lo que hace falta para que rotar
   * el teléfono o achicar la ventana no deje colgado un cartel que ya no aplica.
   */
  const esMobile = useEsMobile();
  const [state, setState] = useState<HomeBannersState>(EMPTY);
  const uidRef = useRef<string | null>(null);
  const companyRef = useRef<string | null>(null);
  /**
   * Contador para reevaluar los pasos sin recargar la página. Lo bumpea
   * `connectTiendanube` al éxito: completar un paso tiene que actualizar la
   * tarjeta —y, si era el último, sacar la felicitación— en el momento.
   */
  const [revision, setRevision] = useState(0);
  /**
   * `false` hasta que la primera evaluación termina —
   * ADR-2026-08-03-home-aparece-entera-o-skeleton.
   *
   * `kind: null` es ambiguo: significa "no va ningún banner" Y "todavía no sé". El
   * consumidor no podía distinguirlos, así que dibujaba la home sin banner y lo
   * metía a mitad de camino, empujando todo lo de abajo. Con esto puede esperar.
   *
   * NO vuelve a `false` en las reevaluaciones (`revision`, overrides de dev): esas
   * corren con la home ya dibujada y apagarla sería un parpadeo peor que el salto.
   */
  const [resuelto, setResuelto] = useState(false);

  // Otra pestaña puede cerrar sesión o entrar con otra cuenta sin que ésta se
  // recargue (el logout de la app sí recarga; ver `lib/cerrarSesion`). Si el usuario
  // ya no es el que evaluó los carteles, se reevalúa todo como la cuenta nueva:
  // mismo criterio que el store de la campana
  // (ADR-2026-09-15-el-store-de-notificaciones-tiene-dueno). `INITIAL_SESSION` se
  // ignora (es el usuario que la evaluación de abajo ya está resolviendo) y el bump
  // se difiere fuera del callback para no tocar la API de auth desde adentro.
  useEffect(() => {
    const { data: auth } = supabase.auth.onAuthStateChange((evento, sesion) => {
      if (evento === "INITIAL_SESSION") return;
      const uid = sesion?.user?.id ?? null;
      if (uid === uidRef.current) return;
      setTimeout(() => setRevision((r) => r + 1), 0);
    });
    return () => auth.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Sin usuario o sin empresa el hook no tiene nada que leer y se va sin pintar banner.
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        uidRef.current = user.id;

        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        const idCompany = profile?.id_company as string | undefined;
        if (!idCompany) return;
        companyRef.current = idCompany;

        const { data: company } = await supabase
          .from("company_profiles")
          .select(
            // `cuestionario_respondido`: vuelve al select con la encuesta
            // (ADR-2026-09-21-la-encuesta-vuelve-a-la-home-con-piso-de-uso). Había
            // salido cuando el cartel se dio de baja
            // (ADR-2026-08-27-tarjeta-configuracion-cuenta-paga); la columna nunca se
            // fue de la DB.
            // `paid_plan_start_date`: ¿ya fue cliente pago? Decide si la barra puede
            // prometer el descuento — ver más abajo.
            "plan, pais, imagenes_restantes:imagenes_disponibles, max_fotos_mes, fecha_cancelacion_programada, fecha_reactivacion_programada, trigger_cancelacion, paid_plan_start_date, cuestionario_respondido",
          )
          .eq("id_company", idCompany)
          .single();
        if (cancelled) return;

        const plan = ((company as any)?.plan || "gratis") as string;
        const pais = ((company as any)?.pais || "") as string;
        const imagenesRestantes =
          typeof (company as any)?.imagenes_restantes === "number"
            ? ((company as any).imagenes_restantes as number)
            : null;
        // Denominador del umbral del 5% (ADR-2026-07-30-solo-criterio-saldo-toda-app).
        const capacidadPlan =
          typeof (company as any)?.max_fotos_mes === "number"
            ? ((company as any).max_fotos_mes as number)
            : null;
        const isArgentina = pais.toLowerCase() === "argentina";
        const isPaidPlan = plan && plan !== "gratis";

        // Estado de la suscripción (ortogonal al banner promocional). Solo
        // aplica a usuarios pagos; un free no tiene cancelación/pausa.
        const cancelRaw = (company as any)?.fecha_cancelacion_programada;
        const resumeRaw = (company as any)?.fecha_reactivacion_programada;
        const triggerRaw = (company as any)?.trigger_cancelacion;
        let status: SubscriptionStatus = null;
        let cancelDate: string | null = null;
        let resumeDate: string | null = null;
        let cancelTrigger: string | null = null;
        if (isPaidPlan && isFuture(cancelRaw)) {
          status = "cancelled";
          cancelDate = formatDate(cancelRaw);
          cancelTrigger = (triggerRaw as string) || null;
        } else if (isPaidPlan && isFuture(resumeRaw)) {
          status = "paused";
          resumeDate = formatDate(resumeRaw);
        }

        if (!isPaidPlan) {
          /* Acá se contaba cuántas imágenes había generado el usuario: el banner
             `upsell` salía a partir de N (ADR-680, bajado a 3 en
             ADR-2026-08-03-upsell-de-home-a-las-3-imagenes). Ese banner ya no existe
             (ADR-2026-08-03-barra-creditos-reemplaza-upsell) y la barra que lo
             reemplaza NO mira las generadas sino el SALDO, así que el `count` se fue
             con él — era una consulta por carga de home que ya no decide nada.

             Decisión de merge (ADR-2026-08-05-la-barra-de-creditos-le-gana-al-upsell):
             `principal` venía de mejorar ese conteo —umbral a 3 y el arreglo del
             filtro `has_error` NULL— y esa mejora se pierde acá, a sabiendas. No es
             que el conteo estuviera mal: es que ya nadie pregunta por él. La barra se
             dispara por saldo bajo, que llega ANTES que las 3 imágenes y no necesita
             una consulta extra. Si alguna vez vuelve un aviso por cantidad generada,
             el filtro correcto es `or("has_error.is.null,has_error.eq.false")` y NO
             `neq("has_error", true)`: en SQL `has_error <> true` da NULL cuando la
             columna es NULL, así que esas filas quedaban fuera del conteo. */
          // El descuento se consulta sólo si la barra va a mostrarse —o sea, si el
          // saldo NO está ok— y sale del mismo cupón que aplica el catálogo: si acá
          // dijéramos un % fijo y el cupón estuviera vencido o con otro
          // multiplicador, prometería un precio que el checkout no cobra
          // (ADR-2026-07-30-banner-home-habla-descuento-real).
          //
          // Y ojo: `plan = 'gratis'` NO alcanza para saber que le corresponde el
          // descuento. Un ex cliente que se dio de baja queda en `gratis` y ya no
          // lleva la promo de captación (ADR-2026-08-18-descuento-no-revive-al-volver-a-gratis):
          // sin este filtro la barra le prometía un 30% que ni el catálogo muestra
          // ni el servidor de pagos cobra. Con `null` cae sola en su copy sin descuento.
          const hayAvisoDeSaldo =
            estadoDeCreditos(imagenesRestantes, capacidadPlan) !== "ok";
          const yaFueClientePago = !!(company as any)?.paid_plan_start_date;
          const cupon =
            hayAvisoDeSaldo && !yaFueClientePago ? await fetchHotSaleCoupon() : null;
          if (cancelled) return;
          setState({
            // Sin cartel propio: el aviso de la prueba gratis es la barra de saldo.
            // Y sin pasos: configurar la cuenta es algo que se le pide a quien ya
            // compró, no a quien todavía está probando.
            kind: null,
            setup: null,
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: cupon
              ? Math.round((1 - cupon.multiplicador) * 100)
              : null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        // Plan PRO o ULTRA: la llamada de capacitación incluida, PRIMERO en la cadena
        // (ADR-2026-08-25-banner-llamada-capacitacion-ultra,
        // ADR-2026-10-06-la-llamada-de-capacitacion-tambien-es-de-pro), antes de la tarjeta de
        // configuración. El flag por
        // usuario lo marcan tanto "No me interesa" como "Agendar" (una vez que
        // tocó cualquiera de los dos, insistir es ruido). `loadOnboardingSeen` es
        // idempotente: para los demás se sigue cargando más abajo, como antes.
        if (incluyeLlamadaDeCapacitacion(plan)) {
          await loadOnboardingSeen(user.id);
          if (cancelled) return;
          if (!isSeen("capacitacion_ultra_banner")) {
            setState({
              kind: "capacitacion",
              // Los pasos de configuración se resuelven recién después, cuando este
              // cartel ya se descartó: mientras tanto no hay tarjeta que mostrar.
              setup: null,
              freeImagesRemaining: imagenesRestantes,
              creditsTotal: capacidadPlan,
              idCompany,
              isArgentina,
              discountPercent: null,
              status,
              cancelDate,
              resumeDate,
              cancelTrigger,
            });
            return;
          }
        }

        // ── Plan pago: los pasos de configuración de la cuenta ─────────────
        // Los DOS se resuelven siempre, aunque el primero esté pendiente: la
        // tarjeta muestra el recorrido completo (lo hecho, lo que falta y cuánto
        // queda), y para eso necesita saber de los dos. Antes se cortaba en el
        // primero que aplicaba porque cada paso era un cartel suelto que no sabía
        // de los otros.

        // Paso 1 — marca. `marcaRecienCompletada` lo da por hecho sin consultar:
        // el análisis lo escribe un webhook que puede tardar (ver el @param).
        let hasAnalysis = marcaRecienCompletada
          ? true
          : getCached<boolean>(CACHE_KEYS.COMPANY_CONFIG);
        if (hasAnalysis === null) {
          try {
            const { data: config } = await supabase
              .from("config")
              .select("media_agent_analysis")
              .eq("id_company", idCompany)
              .maybeSingle();
            let computed = !!config?.media_agent_analysis;
            // Fallback robusto: `media_agent_analysis` puede quedar VACÍO ('')
            // aunque el user YA haya completado el paso (el webhook del análisis
            // a veces devuelve vacío). En ese caso igual lo damos por hecho si la
            // marca DEFAULT ya tiene IDENTIDAD cargada (colores o contexto) — así
            // el paso se cierra cuando la marca está completada de verdad, sin
            // depender de ese flag poco fiable.
            //
            // CUALQUIER marca terminada de la empresa, no sólo la default
            // (ADR-2026-10-19-describir-la-marca-con-el-onboarding-nuevo): el onboarding
            // nuevo y "Añadir nueva marca" dejan la identidad en la marca que escanean, y
            // un miembro con permisos por marca puede no ver la default (la RLS
            // "Sólo las marcas permitidas" es restrictiva). Con sólo la default, quien
            // ya había hecho el onboarding nuevo veía el paso pendiente.
            if (!computed) {
              const { data: marcas } = await supabase
                .from("brand_profiles")
                .select("colors, context")
                .eq("id_company", idCompany)
                .eq("is_draft", false);
              computed = ((marcas ?? []) as { colors?: unknown; context?: unknown }[]).some(
                (b) =>
                  (Array.isArray(b.colors) && b.colors.length > 0) ||
                  (typeof b.context === "string" && b.context.trim().length > 0),
              );
            }
            hasAnalysis = computed;
            // `user.id` y NO `uidRef.current`: el ref es mutable y compartido —
            // leerlo después del `await` daría el usuario NUEVO y la guarda no
            // filtraría nada. `user` es const de esta corrida del efecto.
            //
            // Sólo se guarda el SÍ: el análisis del onboarding termina en segundo plano,
            // y un "todavía no" cacheado dejaba el paso pendiente aunque la marca ya
            // estuviera armada al volver a la home.
            if (hasAnalysis) setCached(CACHE_KEYS.COMPANY_CONFIG, true, user.id);
          } catch {
            hasAnalysis = false;
          }
        }
        if (cancelled) return;
        const marcaForzado =
          pasoForzadoHecho("marca") || pasoForzadoPendiente("marca");
        const marcaHecha = pasoForzadoPendiente("marca")
          ? false
          : !!hasAnalysis || pasoForzadoHecho("marca");

        // Paso 2 — Tienda Nube, si está en `PASOS_ACTIVOS`. Se resuelve SÓLO por el
        // estado real de la integración.
        //
        // El flag `tiendanube_banner` de `onboarding_seen` ya NO cuenta: era el
        // descarte del cartel promocional viejo ("No me interesa"), y acá la tarea no
        // se puede descartar. Dar el paso por resuelto porque alguna vez bajó un
        // cartel dejaba la tarea marcada "Omitido" sin que el usuario haya decidido
        // nada sobre ESTA pantalla. Se sigue escribiendo al conectar, para no romper
        // a quien lo lea.
        //
        // `loadOnboardingSeen` va igual, esté el paso activo o no: de ahí salen los
        // flags de la felicitación.
        const tnActivo = PASOS_ACTIVOS.includes("tiendanube");
        await loadOnboardingSeen(user.id);
        if (cancelled) return;
        let tnConnected: boolean | null = tnActivo
          ? getCached<boolean>(CACHE_KEYS.TN_STATUS)
          : true;
        if (tnConnected === null) {
          try {
            const { data: brandRow } = await supabase
              .from("brand_profiles")
              .select("id")
              .eq("id_company", idCompany)
              .eq("is_default", true)
              .eq("is_draft", false)
              .limit(1)
              .maybeSingle();
            const bId = (brandRow as { id?: string } | null)?.id ?? null;
            const st = await getTnStatus(bId);
            // `companyHasStores`: si CUALQUIER marca de la empresa ya tiene tienda
            // conectada, el paso está hecho (es por empresa, no por marca).
            tnConnected = st.connected || st.companyHasStores;
            setCached(CACHE_KEYS.TN_STATUS, tnConnected, user.id);
          } catch {
            tnConnected = false;
          }
        }
        if (cancelled) return;
        if (tnConnected && tnActivo && !isSeen("tiendanube_banner")) {
          // Ya hay tienda conectada → dejamos el flag puesto, que es lo que miran
          // los consumidores viejos del cartel.
          markSeen("tiendanube_banner", uidRef.current);
        }
        const tnForzado =
          pasoForzadoHecho("tiendanube") || pasoForzadoPendiente("tiendanube");
        const tnHecho = pasoForzadoPendiente("tiendanube")
          ? false
          : !!tnConnected || pasoForzadoHecho("tiendanube");
        /**
         * ¿Algún paso está "hecho" sólo porque lo forzó el override de dev? Entonces
         * NO se escribe nada en `onboarding_seen`: el override promete no tocar la
         * base, y sin esta guarda mirar la felicitación en local le dejaba
         * `setup_pago_ok` puesto a la cuenta de verdad — o sea que cuando el usuario
         * terminara la configuración en serio, ya no habría festejo que mostrarle.
         * (Se descubrió justo así, verificando esta pantalla.)
         */
        const hayPasosForzados = marcaForzado || (tnActivo && tnForzado);

        // "Ahora no": la tarea se da por resuelta sin hacerla. Se mira DESPUÉS de
        // `tnHecho` y sólo si la tarea no está hecha — si el usuario terminó
        // conectando la tienda por otro lado (Mi marca), lo que corresponde es el
        // tilde de completada y no el rótulo de omitida.
        const tnOmitido = !tnHecho && isSeen("setup_tiendanube_omitido");
        const resueltos: Record<SetupPasoId, SetupPaso> = {
          marca: {
            id: "marca",
            hecho: marcaHecha,
            omitido: false,
            omitible: PASOS_OMITIBLES.includes("marca"),
          },
          tiendanube: {
            id: "tiendanube",
            // Omitida cuenta como resuelta: deja de pedir algo y la tarjeta se
            // baja cuando no queda ninguna tarea pendiente. Lo que NO hace es
            // contar como completada — ver `omitido` en el tipo.
            hecho: tnHecho || tnOmitido,
            omitido: tnOmitido,
            omitible: PASOS_OMITIBLES.includes("tiendanube"),
          },
        };
        const pasos: SetupPaso[] = PASOS_ACTIVOS.map((id) => resueltos[id]);
        const faltaAlguno = pasos.some((p) => !p.hecho);

        if (faltaAlguno) {
          // La primera vez que ve la tarjeta con algo pendiente queda registrado:
          // es lo que después habilita la felicitación. Sin esto, quien ya tenía
          // los dos pasos hechos antes de que la tarjeta existiera abriría la home
          // con un "¡configuración completa!" por algo que nunca hizo.
          if (!isSeen("setup_pago_en_curso") && !hayPasosForzados) {
            markSeen("setup_pago_en_curso", uidRef.current);
          }
          setState({
            kind: "setup",
            setup: pasos,
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        // Todo resuelto. Se felicita a quien VENÍA de tenerlo pendiente y todavía no
        // fue felicitado; `celebrandoEnEstaCarga` mantiene la tarjeta en pantalla
        // mientras dure la carga aunque ya la hayamos marcado como vista (el
        // usuario acaba de terminar: que no se le desaparezca al re-evaluar).
        //
        // Con algún paso OMITIDO no hay festejo: "tu cuenta quedó configurada" es
        // falso si el usuario dijo "ahora no" a una de las tareas, y felicitarlo
        // por saltear algo convierte el acuse de recibo en un chiste. La tarjeta se
        // baja igual (ya no queda nada pendiente), sólo que en silencio
        // (ADR-2026-09-15-ahora-no-en-tienda-nube).
        const hayOmitidos = pasos.some((p) => p.omitido);
        const correspondeCelebrar =
          !hayOmitidos &&
          ((celebrandoEnEstaCarga !== null && celebrandoEnEstaCarga === uidRef.current) ||
            (isSeen("setup_pago_en_curso") && !isSeen("setup_pago_ok")));
        if (correspondeCelebrar && celebrandoEnEstaCarga !== uidRef.current) {
          celebrandoEnEstaCarga = uidRef.current;
          // Se marca al MOSTRARLA, no al cerrarla: una felicitación que sobrevive
          // recargas deja de ser un festejo y pasa a ser un cartel más en la home.
          if (!hayPasosForzados) markSeen("setup_pago_ok", uidRef.current);
        }
        /* ── Encuesta de feedback (+10 imágenes) ──────────────────────────
           ADR-2026-09-21-la-encuesta-vuelve-a-la-home-con-piso-de-uso.

           Va DETRÁS de la configuración y de su festejo: acá abajo ya no queda ningún
           paso pendiente (las ramas de arriba cortaron con `return`), así que el
           cartel no le puede robar la atención a una tarea que el usuario tiene a
           medias — que es por lo que la encuesta se había dado de baja
           (ADR-2026-08-27-tarjeta-configuracion-cuenta-paga).

           El conteo corre SÓLO en este punto: cuenta paga (las gratis cortaron mucho
           más arriba), con la configuración terminada y la encuesta sin responder. Es
           un `head: true`, así que no baja ninguna fila. Sin filtrar `active` ni por
           marca — ver `MINIMO_GENERADAS_PARA_ENCUESTA`. */
        const contarGeneradas = async (): Promise<number | null> => {
          try {
            const { count, error } = await supabase
              .from("imagenes_generadas")
              .select("id", { count: "exact", head: true })
              .eq("id_company", idCompany);
            // `count` puede venir `null` sin `error` (PostgREST sin el header de
            // conteo). Eso NO es cero: dejarlo en `null` es lo que hace que el cartel
            // no salga por un conteo que no pasó.
            if (!error && typeof count === "number") return count;
          } catch {
            // Queda en `null`: el cartel no sale esta vuelta.
          }
          return null;
        };
        const encuestaRespondida = (company as any)?.cuestionario_respondido === true;
        let mostrarEncuesta = false;
        if (!encuestaRespondida) {
          const generadas = await contarGeneradas();
          if (cancelled) return;
          mostrarEncuesta = correspondeEncuesta({
            esPago: true,
            respondido: false,
            generadas,
          });
        }

        /* ── Calificar en Tiendanube (+10 imágenes, una vez) ────────────────
           ADR-2026-10-12-premio-por-calificar-en-tiendanube. El escalón siguiente a
           la encuesta: sólo se evalúa con ella respondida.

           "Tienda conectada" es el paso REAL de la configuración: `tnHecho` sin
           `tnOmitido` (el "Ahora no" no es una tienda). Con el paso fuera de
           `PASOS_ACTIVOS` no hay estado real que mirar y el cartel no sale.

           El candado (`calificacion_tiendanube_premiada`) se lee en una consulta
           APARTE y no en el select grande de arriba: si la columna todavía no
           existiera en la base (front desplegado antes que la migración), ese select
           fallaría entero y la home se quedaría sin ningún cartel. Acá, a lo sumo,
           no sale éste. Sin backend nuevo no hay a quién reclamar el premio. */
        let mostrarCalificar = false;
        if (encuestaRespondida && USE_ATLAS_BACKEND && tnActivo && tnHecho) {
          let premiada: boolean | null = null;
          try {
            const { data, error } = await supabase
              .from("company_profiles")
              .select("calificacion_tiendanube_premiada")
              .eq("id_company", idCompany)
              .maybeSingle();
            const v = (data as { calificacion_tiendanube_premiada?: unknown } | null)
              ?.calificacion_tiendanube_premiada;
            if (!error && typeof v === "boolean") premiada = v;
          } catch {
            // Queda en `null`: el cartel no sale esta vuelta.
          }
          if (cancelled) return;
          if (premiada === false) {
            const generadas = await contarGeneradas();
            if (cancelled) return;
            mostrarCalificar = correspondeCalificarTiendanube({
              esPago: true,
              cuestionarioRespondido: true,
              tiendaConectada: true,
              premiada,
              generadas,
            });
          }
        }

        setState({
          /* El orden de este ternario ES la prioridad de lo que queda de la cadena:
             festejo → encuesta → calificar en Tiendanube → "probá en la computadora".
             (Encuesta y calificar no compiten nunca: una exige la encuesta sin
             responder y la otra respondida.)

             El festejo va primero porque es de UNA sola vez y es la respuesta a algo
             que el usuario acaba de hacer; taparlo con otro cartel sería no acusar
             recibo. La encuesta le gana al cartel de escritorio porque ofrece algo
             (10 imágenes) y el otro es una sugerencia.

             El filtro de dispositivo del cartel de escritorio está ACÁ y no en una
             clase `sm:hidden` del render: `kind` es uno solo, y un cartel que ganara
             en desktop para esconderse por CSS dejaría un hueco invisible
             (ADR-2026-08-25-cartel-probar-atlas-en-escritorio). */
          kind: correspondeCelebrar
            ? "setup_ok"
            : mostrarEncuesta
              ? "cuestionario"
              : mostrarCalificar
                ? "calificar_tiendanube"
                : esMobile && !isSeen("probar_escritorio_banner")
                  ? "escritorio"
                  : null,
          setup: pasos,
          freeImagesRemaining: imagenesRestantes,
          creditsTotal: capacidadPlan,
          idCompany,
          isArgentina,
          discountPercent: null,
          status,
          cancelDate,
          resumeDate,
          cancelTrigger,
        });
      } catch {
        // Si la consulta falla (sesión vencida, permisos) la home queda sin banner.
        if (!cancelled) setState(EMPTY);
      }
    })().finally(() => {
      // Un solo lugar para avisar "ya sé qué banner va" — el cuerpo de arriba tiene
      // cuatro salidas (sin usuario, sin empresa, éxito, error) y marcarlo en cada
      // una es olvidárselo en la quinta. Ver `resuelto` en el tipo del estado.
      if (!cancelled) setResuelto(true);
    });
    return () => {
      cancelled = true;
    };
  }, [marcaRecienCompletada, revision, esMobile]);

  /**
   * Cartel de la llamada de capacitación Ultra, resuelto — lo llaman "No me
   * interesa" Y "Agendar" (ADR-2026-08-25-banner-llamada-capacitacion-ultra).
   * Marca el flag por usuario (DB), baja el cartel ya y **reevalúa la cadena**
   * para que el paso siguiente (marca / encuesta / …) aparezca en el acto —
   * mismo criterio que `dismissEscritorio` (ADR-889).
   */
  const dismissCapacitacion = useCallback(() => {
    markSeen("capacitacion_ultra_banner", uidRef.current);
    setState((s) => ({ ...s, kind: null }));
    setRevision((r) => r + 1);
  }, []);

  /**
   * "Ahora no". Escribe el flag SIEMPRE (aunque haya overrides de dev dando vueltas):
   * es una decisión del usuario sobre su cuenta, no un estado que estemos simulando
   * —la guarda `hayPasosForzados` protege los flags que la app escribe SOLA, no los
   * que el usuario aprieta—. Después reevalúa, igual que al conectar: si era la
   * única tarea pendiente, la tarjeta se baja sin recargar.
   */
  const omitirPaso = useCallback((id: SetupPasoId) => {
    const llave = LLAVE_OMITIDO[id];
    if (!llave) return; // paso no omitible: no hay nada que registrar
    markSeen(llave, uidRef.current);
    setRevision((r) => r + 1);
  }, []);

  // Cierra la felicitación (ya quedó marcada como vista al mostrarse).
  const cerrarCelebracion = useCallback(() => {
    celebrandoEnEstaCarga = null;
    setState((s) => ({ ...s, kind: null }));
  }, []);

  // "Conectar": mismo flujo OAuth que Mi marca (popup + espera),
  // sobre la marca DEFAULT (igual que el chequeo que resuelve el paso).
  // Al éxito: cache TN a "conectada", flag resuelto y reevaluación de los pasos.
  // Si lanza (cancelado / popup bloqueado), el paso queda pendiente.
  const connectTiendanube = useCallback(async () => {
    const idCompany = companyRef.current;
    if (!idCompany) throw new Error("Empresa no disponible.");
    // ⚠️ La ventana del OAuth se abre ACÁ, sincrónicamente dentro del click y
    // ANTES del `await` que busca la marca default: Safari (y todos los
    // navegadores de iOS) la bloquean si se la pide después de una espera —
    // ver ADR-2026-08-07-tiendanube-popup-safari.
    const popup = abrirVentanaOAuth();
    // Dueño de la sesión ANTES de los awaits (la marca default + toda la vuelta
    // del OAuth, que puede tardar). Si al volver cambió, el `setCached` de abajo
    // se descarta solo en vez de contarle a otra cuenta que tiene Tienda Nube.
    const duenio = cacheOwner();
    let bId: string | null = null;
    try {
      const { data: brandRow } = await supabase
        .from("brand_profiles")
        .select("id")
        .eq("id_company", idCompany)
        .eq("is_default", true)
        .eq("is_draft", false)
        .limit(1)
        .maybeSingle();
      bId = (brandRow as { id?: string } | null)?.id ?? null;
    } catch (e) {
      try {
        popup?.close(); // no dejar la ventana de carga colgada
      } catch {
        /* noop */
      }
      throw e;
    }
    await connectTnEnVentana(bId, popup);
    // Conectar trae el catálogo (ADR-2026-10-01-conectar-desde-atlas-trae-el-catalogo).
    void traerCatalogoAlConectar(bId);
    setCached(CACHE_KEYS.TN_STATUS, true, duenio);
    markSeen("tiendanube_banner", uidRef.current);
    // Se REEVALÚA en vez de bajar la tarjeta: el paso se marca hecho, la barra
    // avanza y —si era el último— sale la felicitación, todo sin recargar. Bajarla
    // de una dejaba al usuario sin acuse de recibo de lo que acababa de hacer.
    setRevision((r) => r + 1);
  }, []);

  /**
   * "No me interesa" del cartel de escritorio — y también cerrar la pantalla que
   * abre "Probar": una vez que el usuario leyó el mensaje, repetírselo es
   * insistir. Marca el flag por usuario (DB) y reevalúa para que la home quede
   * sin cartel en el acto (ADR-2026-08-25-cartel-probar-atlas-en-escritorio).
   */
  const dismissEscritorio = useCallback(() => {
    markSeen("probar_escritorio_banner", uidRef.current);
    setState((s) => ({ ...s, kind: null }));
    setRevision((r) => r + 1);
  }, []);

  /**
   * Encuesta respondida: se baja el cartel y se **reevalúa la cadena**, para que lo
   * que venía detrás (la tarjeta de configuración, si le queda algo) aparezca en el
   * momento y no en la próxima carga de la home.
   */
  const dismissCuestionario = useCallback(() => {
    setState((s) => ({ ...s, kind: null }));
    setRevision((r) => r + 1);
  }, []);

  /** Premio de calificar reclamado: mismo criterio que la encuesta. */
  const dismissCalificarTiendanube = useCallback(() => {
    setState((s) => ({ ...s, kind: null }));
    setRevision((r) => r + 1);
  }, []);

  return {
    ...state,
    resuelto,
    connectTiendanube,
    cerrarCelebracion,
    omitirPaso,
    dismissCapacitacion,
    dismissEscritorio,
    dismissCuestionario,
    dismissCalificarTiendanube,
  };
}
